<script lang="ts">
import type { Snippet } from 'svelte';
import { createEventDispatcher } from 'svelte';
import type { ClassValue } from 'svelte/elements';

import { Button } from '../button';
import { Icon, type IconProps } from '../icon';
import { Progress } from '../progress';
import { BannerVariant, type BannerVariantType } from './variants';

type Size = 'sm' | 'base';

interface Props {
  /** The severity of the notification you want to show users*/
  variant: BannerVariantType;
  /** The scaling applied on the y axis of the page*/
  progress?: number;
  /** If true a user can close out of the notification. If false they cannot*/
  exitable?: boolean;
  /** The size of the banner*/
  size?: Size;
  /** Change the icon to a loading spinner */
  loading?: boolean;
  /** Additional CSS classes to pass to the banner. */
  class?: ClassValue;
  titleContent?: Snippet;
  subtitle?: Snippet;
  action?: Snippet;
  actionEmphasize?: Snippet;
  expandableDetails?: Snippet;
}

/* eslint-disable prefer-const */
let {
  variant = 'info',
  progress = 1,
  exitable = false,
  size = 'base',
  class: extraClasses,
  loading = false,
  titleContent,
  subtitle,
  action,
  actionEmphasize,
  expandableDetails,
}: Props = $props();

const dispatch = createEventDispatcher<{
  /** when the notification close button is pressed*/
  close: null;
}>();

const handleClose = () => dispatch('close');

const isInfo = $derived(variant === BannerVariant.Info);
const isWarn = $derived(variant === BannerVariant.Warning);
const isDanger = $derived(variant === BannerVariant.Danger);
const isSuccess = $derived(variant === BannerVariant.Success);
const isPending = $derived(variant === BannerVariant.Pending);
const fontSize = $derived(size === 'sm' ? 'text-xs' : 'text-sm');
const iconProps = $derived.by<IconProps | undefined>(() => {
  switch (variant) {
    case BannerVariant.Info: {
      return { name: 'information', cx: 'text-info-dark' };
    }
    case BannerVariant.Warning: {
      return { name: 'alert', cx: 'text-warning-bright' };
    }
    case BannerVariant.Danger: {
      return { name: 'alert-circle', cx: 'text-danger-dark' };
    }
    case BannerVariant.Success: {
      return { name: 'check-circle', cx: 'text-success-dark' };
    }
    case BannerVariant.Pending: {
      return undefined;
    }
  }
});

let openDetails = $state(false);
const toggleDetails = () => (openDetails = !openDetails);
</script>

<div
  class={[
    'flex border',
    {
      'border-info-medium bg-info-light': isInfo,
      'border-warning-medium bg-warning-light': isWarn,
      'border-success-medium bg-success-light': isSuccess,
      'border-danger-medium bg-danger-light': isDanger,
      'border-[#d3c1f6] bg-[#f5ebff]': isPending,
    },
    extraClasses,
  ]}
>
  <div class="relative flex-col">
    <div
      class={[
        'absolute top-0 left-0 -mt-px -ml-px h-[calc(100%+2px)] w-[3px] origin-bottom',
        {
          'bg-[#80b3e5]': isInfo,
          'bg-[#eed59f]': isWarn,
          'bg-[#9ebe9f]': isSuccess,
          'bg-[#df9a9b]': isDanger,
          'bg-[#f5ebff]': isPending,
        },
      ]}
    ></div>
    <div
      style="transform: scale(1, {progress})"
      class={[
        '-mt-px -ml-px h-[calc(100%+2px)] w-[3px] origin-bottom',
        {
          'bg-info-dark': isInfo,
          'bg-warning-bright': isWarn,
          'bg-success-dark': isSuccess,
          'bg-danger-dark': isDanger,
          'bg-[#7c2fca]': isPending,
        },
      ]}
    ></div>
  </div>

  <div class="w-full">
    <div
      class={[
        'relative flex w-full justify-between gap-2',
        {
          'p-3 pr-10': size === 'base',
          'px-4 py-3': size === 'sm',
        },
      ]}
    >
      <div class="flex gap-3">
        {#if loading && size !== 'sm'}
          <div>
            <Progress />
          </div>
        {:else if iconProps && size !== 'sm'}
          <div class="w-[18px]">
            <Icon
              size="lg"
              {...iconProps}
            />
          </div>
        {/if}

        <div class="flex flex-col break-words">
          {#if titleContent}
            <p class="{fontSize} text-default font-semibold break-words">
              {@render titleContent()}
            </p>
          {/if}

          <div class="text-subtle-1 flex flex-col gap-2">
            {#if subtitle}
              <p class="{fontSize} break-words">
                {@render subtitle()}
              </p>
            {/if}

            {#if action}
              <div class="{fontSize} font-medium hover:underline">
                {@render action()}
              </div>
            {/if}

            {#if actionEmphasize}
              <div class="mt-2 mb-1 {fontSize}">
                {@render actionEmphasize()}
              </div>
            {/if}
          </div>
        </div>

        {#if exitable}
          <Button
            variant="ghost"
            class="text-gray-7 absolute top-1 right-1 pr-1.5 pl-1.5"
            aria-label="Dismiss notification"
            onclick={handleClose}
          >
            <Icon name="close" />
          </Button>
        {/if}
      </div>
    </div>
    {#if expandableDetails}
      <div
        class={[
          'flex flex-col gap-3 border-t p-3 text-xs',
          {
            'border-info-medium': isInfo,
            'border-warning-medium': isWarn,
            'border-success-medium': isSuccess,
            'border-danger-medium': isDanger,
            'border-[#d3c1f6]': isPending,
          },
        ]}
      >
        <button
          onclick={toggleDetails}
          class="flex items-center gap-1"
        >
          <div
            class={['text-gray-6 transition-all', openDetails && 'rotate-90']}
          >
            <Icon name="chevron-right" />
          </div>
          <span class="text-subtle-1">Details</span>
        </button>
        {#if openDetails}
          {@render expandableDetails()}
        {/if}
      </div>
    {/if}
  </div>
</div>
